import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getTemplate, saveTemplate, applyTemplate } from "../services/api";
import HelpModal from "../components/HelpModal";
import { Rnd } from "react-rnd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import 'dayjs/locale/es';

dayjs.extend(customParseFormat);
dayjs.locale('es');

const ZOOM_LEVELS = [15, 22, 30, 45, 60];
const SNAP_INTERVAL_MIN = 15;
const TOTAL_SLOTS = 24 * (60 / SNAP_INTERVAL_MIN);
const MAX_DESC_LENGTH = 250;

const REFERENCE_DATES = [
  "2000-01-03",
  "2000-01-04",
  "2000-01-05",
  "2000-01-06",
  "2000-01-07",
  "2000-01-08",
  "2000-01-09",
];
const DAY_NAMES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const safeParse = (dateStr) => {
  const cleaned = dateStr?.trim().replace(/\r|\n/g, "");
  const parsed = dayjs(cleaned, "YYYY/MM/DD HH:mm:ss", true);
  return parsed.isValid() ? parsed : null;
};

const parseDurationToMinutes = (durationStr) => {
  if (typeof durationStr !== 'string' || !durationStr.includes(':')) return 0;
  const parts = durationStr.split(":").map(Number);
  if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return 0;
  return parts[0] * 60 + parts[1];
};

const formatMinutesToDuration = (totalMinutes) => {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${m.toString().padStart(2, "0")}:00`;
};

const getDurationMinutes = (p) => {
  const start = safeParse(p.start);
  const stop = safeParse(p.stop);
  if (!start || !stop) return parseDurationToMinutes(p.duration || "0:00:00");
  return stop.diff(start, 'minute');
};

const generateId = () => `id-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

const serializeForComparison = (progs) =>
  JSON.stringify(
    [...progs].sort((a, b) => a.start.localeCompare(b.start))
              .map(({ __internalId, ...rest }) => rest)
  );

const validateDayComplete = (dayProgs) => {
  if (!dayProgs.length) return false;
  const sorted = [...dayProgs].sort((a, b) => safeParse(a.start).diff(safeParse(b.start)));
  const firstStart = safeParse(sorted[0].start);
  if (!firstStart || firstStart.hour() !== 0 || firstStart.minute() !== 0 || firstStart.second() !== 0) return false;
  for (let i = 0; i < sorted.length - 1; i++) {
    const stop = safeParse(sorted[i].stop);
    const nextStart = safeParse(sorted[i + 1].start);
    if (!stop || !nextStart || stop.diff(nextStart, 'second') !== 0) return false;
  }
  const lastStop = safeParse(sorted[sorted.length - 1].stop);
  const dayStart = safeParse(sorted[0].start);
  if (!lastStop || !dayStart) return false;
  const expectedEnd = dayStart.startOf('day').add(1, 'day');
  return lastStop.diff(expectedEnd, 'second') === 0;
};

const computeDaysComplete = (allProgs) =>
  REFERENCE_DATES.reduce((acc, dateStr, idx) => {
    const prefix = dateStr.replace(/-/g, '/');
    acc[idx] = validateDayComplete(allProgs.filter(p => p.start.startsWith(prefix)));
    return acc;
  }, {});

const computePositions = (programList, sh) => {
  const newPosMap = {};
  programList.forEach((p) => {
    const start = safeParse(p.start);
    const dur = getDurationMinutes(p);
    if (start && start.isValid() && p.__internalId) {
      newPosMap[p.__internalId] = {
        y: Math.round((start.hour() * 60 + start.minute()) / SNAP_INTERVAL_MIN) * sh,
        height: Math.max(sh, Math.round(dur / SNAP_INTERVAL_MIN) * sh),
      };
    }
  });
  return newPosMap;
};

export default function TemplateEditor() {
  const [programmes, setProgrammes] = useState([]);
  const [positions, setPositions] = useState({});
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [daysComplete, setDaysComplete] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [savedJson, setSavedJson] = useState('');
  const [pendingAddY, setPendingAddY] = useState(null);
  const [pendingAdd, setPendingAdd] = useState(null);
  const [editingProgram, setEditingProgram] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [channelDisplayName, setChannelDisplayName] = useState("Canal");
  const [zoomIdx, setZoomIdx] = useState(2);
  const [viewMode, setViewMode] = useState('day');
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const slotHeight = ZOOM_LEVELS[zoomIdx];
  const programmesRef = useRef(programmes);
  programmesRef.current = programmes;

  const navigate = useNavigate();
  const currentDate = REFERENCE_DATES[currentDayIndex];

  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  const calculatePositions = useCallback((programList) => {
    setPositions(computePositions(programList, ZOOM_LEVELS[zoomIdx]));
  }, [zoomIdx]);

  useEffect(() => {
    const sh = ZOOM_LEVELS[zoomIdx];
    setPositions(computePositions(programmesRef.current, sh));
  }, [zoomIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!savedJson) return;
    setHasChanges(serializeForComparison(programmes) !== savedJson);
  }, [programmes, savedJson]);

  const updateState = useCallback((newList) => {
    setProgrammes(newList);
    setDaysComplete(computeDaysComplete(newList));
    calculatePositions(newList);
  }, [calculatePositions]);

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (!userStr) { navigate('/'); return; }
    let user;
    try { user = JSON.parse(userStr); } catch { navigate('/'); return; }
    if (!user?.token || !user?.file) { navigate('/'); return; }

    setChannelDisplayName(user.channel_title || user.file.split('.')[0]);

    getTemplate(user.file)
      .then(res => {
        const list = (res.data.programmes || []).map(p => ({ ...p, __internalId: generateId() }));
        setProgrammes(list);
        setDaysComplete(computeDaysComplete(list));
        setPositions(computePositions(list, ZOOM_LEVELS[zoomIdx]));
        setSavedJson(serializeForComparison(list));
      })
      .catch(() => showNotification("Error al cargar la plantilla.", "error"));
  }, [navigate, showNotification]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentDateProgs = programmes.filter(p => p.start.startsWith(currentDate.replace(/-/g, '/')));

  const handleAdd = () => {
    if (pendingAddY === null) return;
    const minutes = Math.round(pendingAddY / slotHeight) * SNAP_INTERVAL_MIN;
    const duration = SNAP_INTERVAL_MIN;
    const newStart = dayjs(currentDate).startOf('day').add(minutes, 'minute');
    const newStop = newStart.add(duration, 'minute');

    const newStartMin = minutes;
    const newEndMin = minutes + duration;
    const overlaps = currentDateProgs.some(p => {
      const s = safeParse(p.start);
      if (!s) return false;
      const sm = s.hour() * 60 + s.minute();
      const em = sm + getDurationMinutes(p);
      return newStartMin < em && newEndMin > sm;
    });
    if (overlaps) { showNotification("❌ Ese horario ya está ocupado.", "error"); setPendingAddY(null); return; }

    const newProg = {
      title: "Nuevo programa", desc: "",
      start: newStart.format("YYYY/MM/DD HH:mm:ss"),
      stop: newStop.format("YYYY/MM/DD HH:mm:ss"),
      duration: formatMinutesToDuration(duration),
      __internalId: generateId(),
    };
    updateState([...programmes, newProg]);
    setPendingAddY(null);
    showNotification("✨ Programa añadido.", "info");
  };

  const handleAddForDayIdx = (dayIdx, y) => {
    const refDate = REFERENCE_DATES[dayIdx];
    const minutes = Math.round(y / slotHeight) * SNAP_INTERVAL_MIN;
    const duration = SNAP_INTERVAL_MIN;
    const newStart = dayjs(refDate).startOf('day').add(minutes, 'minute');
    const newStop = newStart.add(duration, 'minute');

    const prefix = refDate.replace(/-/g, '/');
    const dayProgs = programmes.filter(p => p.start.startsWith(prefix));
    const newStartMin = minutes;
    const newEndMin = minutes + duration;
    const overlaps = dayProgs.some(p => {
      const s = safeParse(p.start);
      if (!s) return false;
      const sm = s.hour() * 60 + s.minute();
      const em = sm + getDurationMinutes(p);
      return newStartMin < em && newEndMin > sm;
    });
    if (overlaps) { showNotification("❌ Ese horario ya está ocupado.", "error"); setPendingAdd(null); return; }

    const newProg = {
      title: "Nuevo programa", desc: "",
      start: newStart.format("YYYY/MM/DD HH:mm:ss"),
      stop: newStop.format("YYYY/MM/DD HH:mm:ss"),
      duration: formatMinutesToDuration(duration),
      __internalId: generateId(),
    };
    updateState([...programmes, newProg]);
    setPendingAdd(null);
    showNotification("✨ Programa añadido.", "info");
  };

  const updateProgram = useCallback((p, newY, newHeight) => {
    const id = p.__internalId;
    const sh = ZOOM_LEVELS[zoomIdx];
    const minutes = Math.round(newY / sh) * SNAP_INTERVAL_MIN;
    const durationMins = Math.max(SNAP_INTERVAL_MIN, Math.round(newHeight / sh) * SNAP_INTERVAL_MIN);

    const progDateSlash = safeParse(p.start)?.format("YYYY/MM/DD") || REFERENCE_DATES[currentDayIndex].replace(/-/g, '/');
    const progDateDash = progDateSlash.replace(/\//g, '-');
    const newStart = dayjs(progDateDash).startOf('day').add(minutes, 'minute');
    const newStop = newStart.add(durationMins, 'minute');

    const current = programmes.find(pr => pr.__internalId === id);
    if (!current) return;
    const curStart = safeParse(current.start);
    const curDur = getDurationMinutes(current);
    if (curStart &&
        Math.round((curStart.hour() * 60 + curStart.minute()) / SNAP_INTERVAL_MIN) * sh === newY &&
        Math.round(curDur / SNAP_INTERVAL_MIN) * sh === newHeight) return;

    const overlaps = programmes.some(other => {
      if (other.__internalId === id) return false;
      const os = safeParse(other.start);
      if (!os || !other.start.startsWith(progDateSlash)) return false;
      const osm = os.hour() * 60 + os.minute();
      const oem = osm + getDurationMinutes(other);
      return minutes < oem && (minutes + durationMins) > osm;
    });
    if (overlaps) { showNotification("❌ Se solapa con otro programa.", "error"); return; }

    updateState(programmes.map(pr =>
      pr.__internalId === id
        ? { ...pr, start: newStart.format("YYYY/MM/DD HH:mm:ss"), stop: newStop.format("YYYY/MM/DD HH:mm:ss"), duration: formatMinutesToDuration(durationMins) }
        : pr
    ));
  }, [currentDayIndex, programmes, showNotification, updateState, zoomIdx]);

  const deleteProgram = useCallback((id) => {
    updateState(programmes.filter(p => p.__internalId !== id));
    showNotification("🗑️ Programa eliminado.", "success");
  }, [programmes, updateState, showNotification]);

  const openEdit = (p) => { setEditingProgram(p); setEditTitle(p.title); setEditDesc(p.desc || ""); setShowModal(true); };

  const saveEdit = () => {
    if (!editingProgram) return;
    updateState(programmes.map(p => p.__internalId === editingProgram.__internalId ? { ...p, title: editTitle, desc: editDesc } : p));
    setShowModal(false);
    showNotification("✏️ Cambios guardados.", "success");
  };

  const handleSave = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?.file) return;
    try {
      const response = await saveTemplate(user.file, programmes);
      setSavedJson(serializeForComparison(programmes));
      const diff = response.data?.diff;
      if (diff?.has_differences) {
        setDiffData(diff);
        setShowDiffModal(true);
      } else {
        showNotification("💾 Plantilla guardada.", "success");
      }
    } catch {
      showNotification("🚫 Error al guardar la plantilla.", "error");
    }
  };

  const handleDiffOverwrite = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?.file || !diffData) return;
    setShowDiffModal(false);
    let successCount = 0;
    for (const week of diffData.weeks) {
      try { await applyTemplate(user.file, week.monday, 'overwrite'); successCount++; } catch (_) {}
    }
    showNotification(`💾 Plantilla guardada y aplicada a ${successCount} semana${successCount !== 1 ? 's' : ''}.`, "success");
    setDiffData(null);
  };

  const handleDiffDismiss = () => {
    setShowDiffModal(false);
    showNotification("💾 Plantilla guardada. Diferencias mantenidas.", "success");
    setDiffData(null);
  };

  const handleRestore = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?.file) return;
    getTemplate(user.file)
      .then(res => {
        const list = (res.data.programmes || []).map(p => ({ ...p, __internalId: generateId() }));
        setProgrammes(list);
        setDaysComplete(computeDaysComplete(list));
        setPositions(computePositions(list, ZOOM_LEVELS[zoomIdx]));
        setSavedJson(serializeForComparison(list));
        showNotification("❌ Cambios descartados.", "info");
      })
      .catch(() => showNotification("🚫 Error al recargar la plantilla.", "error"));
  };

  const renderProgram = (p, idx, compact) => {
    const pos = positions[p.__internalId];
    const start = safeParse(p.start);
    if (!pos || !start) return null;
    const dur = getDurationMinutes(p);
    const color = idx % 2 === 0 ? "bg-emerald-950 hover:bg-emerald-800" : "bg-slate-700 hover:bg-emerald-800";

    return (
      <Rnd key={p.__internalId}
        size={{ width: "100%", height: pos.height }}
        position={{ x: 0, y: pos.y }}
        bounds="parent" dragAxis="y"
        dragGrid={[1, slotHeight]} resizeGrid={[0, slotHeight]}
        enableResizing={{ top: false, right: false, bottom: true, left: false, topRight: false, bottomRight: false, bottomLeft: false, topLeft: false }}
        onDragStop={(e, d) => updateProgram(p, Math.round(d.y / slotHeight) * slotHeight, pos.height)}
        onResizeStop={(e, dir, ref, delta, posSnap) => {
          const newH = Math.max(slotHeight, Math.round(ref.offsetHeight / slotHeight) * slotHeight);
          updateProgram(p, Math.round(posSnap.y / slotHeight) * slotHeight, newH);
        }}
        className={`absolute rounded shadow overflow-hidden text-white transition-colors ${color} ${compact ? 'group' : 'px-3 py-2'}`}
        style={{ border: '1px solid #6366f1', zIndex: 30 }}>
        {compact ? (
          <div className="relative w-full h-full overflow-hidden">
            <div className="px-1 py-0.5 text-xs font-semibold truncate leading-tight">{p.title}</div>
            {pos.height >= slotHeight * 2 && (
              <div className="px-1 text-[10px] opacity-80">{start.format("HH:mm")}</div>
            )}
            {pos.height >= slotHeight * 3 && p.desc && (
              <div className="px-1 text-[10px] opacity-60 leading-tight truncate mt-0.5">{p.desc}</div>
            )}
            <div className="absolute top-0 right-0 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-bl">
              <button onClick={(e) => { e.stopPropagation(); if (pos.height > slotHeight) updateProgram(p, pos.y, pos.height - slotHeight); }} disabled={pos.height <= slotHeight} className="text-white hover:text-orange-300 p-0.5 disabled:opacity-30" title="-15 min">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                  <line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </button>
              <button onClick={(e) => { e.stopPropagation(); updateProgram(p, pos.y, pos.height + slotHeight); }} className="text-white hover:text-green-300 p-0.5" title="+15 min">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  <line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </button>
              <button onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                className="text-white hover:text-yellow-200 p-0.5 text-xs" title="Editar">✏</button>
              <button onClick={(e) => { e.stopPropagation(); deleteProgram(p.__internalId); }}
                className="text-white hover:text-red-300 p-0.5 text-xs" title="Eliminar">✕</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-start">
              <div className="font-bold text-sm truncate">{p.title}</div>
              <div className="flex gap-2">
                <button onClick={(e) => { e.stopPropagation(); if (pos.height > slotHeight) updateProgram(p, pos.y, pos.height - slotHeight); }} disabled={pos.height <= slotHeight} className="text-white hover:text-orange-300 p-0.5 rounded-full hover:bg-black/20 disabled:opacity-30" title="-15 min">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    <line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
                <button onClick={(e) => { e.stopPropagation(); updateProgram(p, pos.y, pos.height + slotHeight); }} className="text-white hover:text-green-300 p-0.5 rounded-full hover:bg-black/20" title="+15 min">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    <line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
                <button onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                  className="text-white hover:text-yellow-200 p-0.5 rounded-full hover:bg-black/20" title="Editar">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button onClick={(e) => { e.stopPropagation(); deleteProgram(p.__internalId); }}
                  className="text-white hover:text-red-300 p-0.5 rounded-full hover:bg-black/20" title="Eliminar">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="text-xs opacity-90">
              {start.format("HH:mm")} → {start.add(dur, "minute").format("HH:mm")}
            </div>
            {pos.height >= slotHeight * 2 && p.desc && (
              <div className="text-xs opacity-60 mt-1 line-clamp-2 leading-snug">{p.desc}</div>
            )}
          </>
        )}
      </Rnd>
    );
  };

  const allComplete = Object.values(daysComplete).length === 7 && Object.values(daysComplete).every(Boolean);
  const headerHeight = allComplete ? '112px' : '130px';

  return (
    <>
      {notification && (
        <div className={`fixed top-4 right-4 z-[1000] px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 text-sm font-semibold
          ${notification.type === 'success' ? 'bg-green-600 text-white' : ''}
          ${notification.type === 'error'   ? 'bg-red-600 text-white'   : ''}
          ${notification.type === 'warning' ? 'bg-yellow-500 text-gray-900' : ''}
          ${notification.type === 'info'    ? 'bg-blue-500 text-white'  : ''}`}>
          {notification.message}
        </div>
      )}

      {/* ── SIDEBAR ── */}
      <div className="fixed left-0 top-0 bottom-0 w-52 bg-gray-900 text-white z-50 flex flex-col overflow-y-auto shadow-2xl">

        {/* Cabecera */}
        <div className="px-4 py-3 border-b border-gray-700 bg-gray-900">
          <div className="flex items-center gap-2">
            <span className="text-lg">📋</span>
            <div>
              <div className="font-bold text-sm leading-tight">Plantilla Semanal</div>
              <div className="text-[11px] text-gray-400">{channelDisplayName}</div>
            </div>
          </div>
        </div>

        {/* ── SECCIÓN ACTIVA: PLANTILLA ── */}
        <div className="border-l-4 border-emerald-500 bg-gray-800">
          <div className="px-3 py-2.5 bg-gray-700 flex items-center gap-2 border-b border-gray-600">
            <span className="text-base">📋</span>
            <span className="text-sm font-bold text-emerald-300">Plantilla</span>
            <span className="ml-auto text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider font-semibold">activo</span>
          </div>
          <div className="p-3 space-y-3">

            {/* Guardar / Restaurar */}
            <div className="flex gap-1">
              <button onClick={handleSave} disabled={!hasChanges}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-30 disabled:cursor-not-allowed text-white text-xs py-1.5 rounded font-medium">
                💾 Guardar
              </button>
              <button onClick={handleRestore} disabled={!hasChanges} title="Descartar cambios"
                className="bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed text-white text-xs px-2.5 py-1.5 rounded">
                ↺
              </button>
            </div>

            {/* Estado días */}
            <div className="border-t border-gray-700 pt-2">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1.5">Estado por día</div>
              <div className="grid grid-cols-7 gap-0.5">
                {DAY_NAMES.map((name, idx) => {
                  const complete = daysComplete[idx];
                  const active = idx === currentDayIndex && viewMode === 'day';
                  return (
                    <button key={idx} onClick={() => { setCurrentDayIndex(idx); setViewMode('day'); }}
                      className={`flex flex-col items-center py-1 rounded text-[9px] transition
                        ${active ? 'bg-emerald-500 text-white font-bold' : 'hover:bg-gray-700 text-gray-300'}`}>
                      <span className="uppercase leading-tight">{name[0]}</span>
                      <span className="leading-tight">{complete ? '✅' : '❌'}</span>
                    </button>
                  );
                })}
              </div>
              {!allComplete && (
                <p className="text-[10px] text-amber-400 mt-1.5 leading-tight">
                  ⚠️ Completá los 7 días para acceder al editor.
                </p>
              )}
            </div>

            {/* Vista y zoom */}
            <div className="space-y-1.5 pt-1 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-500 uppercase tracking-wide">Zoom</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setZoomIdx(i => Math.max(0, i - 1))} disabled={zoomIdx === 0}
                    className="bg-gray-700 hover:bg-gray-600 disabled:opacity-30 text-white w-5 h-5 rounded text-xs flex items-center justify-center">−</button>
                  <span className="text-[10px] text-gray-400 w-8 text-center">{Math.round((slotHeight / 30) * 100)}%</span>
                  <button onClick={() => setZoomIdx(i => Math.min(ZOOM_LEVELS.length - 1, i + 1))} disabled={zoomIdx === ZOOM_LEVELS.length - 1}
                    className="bg-gray-700 hover:bg-gray-600 disabled:opacity-30 text-white w-5 h-5 rounded text-xs flex items-center justify-center">+</button>
                </div>
              </div>
              <button onClick={() => setViewMode(v => v === 'day' ? 'week' : 'day')}
                className={`w-full text-xs py-1.5 rounded transition ${viewMode === 'week' ? 'bg-emerald-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-200'}`}>
                {viewMode === 'week' ? '📺 Vista por día' : '📅 Vista semanal'}
              </button>
            </div>
          </div>
        </div>

        {/* ── SEPARADOR DE NAVEGACIÓN ── */}
        <div className="flex items-center gap-2 px-3 pt-3 pb-1">
          <div className="flex-1 border-t border-gray-700/70" />
          <span className="text-[9px] text-gray-600 uppercase tracking-widest whitespace-nowrap">Ir a</span>
          <div className="flex-1 border-t border-gray-700/70" />
        </div>

        {/* ── NAV: PROGRAMACIÓN ── */}
        <button
          onClick={() => allComplete ? navigate('/editor') : showNotification("⚠️ Completá los 7 días antes de ir al editor.", "warning")}
          className={`w-full flex items-center gap-3 px-4 py-3 border-l-4 transition text-sm font-medium
            ${allComplete ? 'border-orange-900 bg-orange-950 hover:border-orange-500 hover:bg-gray-800 text-orange-300 hover:text-white' : 'border-transparent opacity-30 cursor-not-allowed text-gray-600'}`}>
          <span className="text-base">📡</span>
          <span>Programación</span>
          <span className="ml-auto">{allComplete ? '›' : '🔒'}</span>
        </button>

        {/* ── NAV: AYUDA ── */}
        <button onClick={() => setShowHelp(true)}
          className="w-full flex items-center gap-3 px-4 py-3 border-l-4 border-transparent hover:border-gray-500 hover:bg-gray-800 text-gray-400 hover:text-white transition">
          <span className="text-base">❓</span>
          <span className="text-sm font-medium">Ayuda</span>
          <span className="ml-auto text-gray-600 text-sm">›</span>
        </button>

        {/* Salir */}
        <div className="mt-auto p-3">
          <button onClick={() => { localStorage.removeItem('user'); navigate('/'); }}
            className="w-full bg-red-800 hover:bg-red-700 text-white text-xs py-2 rounded transition">
            Salir
          </button>
        </div>
      </div>

      {/* ── CONTENIDO PRINCIPAL ── */}
      <div className="ml-52 bg-gray-100 min-h-screen font-sans">

        {/* WEEK VIEW */}
        {viewMode === 'week' && (
          <div className="p-4">
            <div className="rounded shadow border border-gray-200">
              <div className="flex border-b-2 border-emerald-700 bg-emerald-700 sticky top-0 z-40 rounded-t overflow-hidden">
                <div className="w-16 shrink-0 border-r border-gray-200" />
                {DAY_NAMES.map((name, idx) => {
                  const active = idx === currentDayIndex;
                  const complete = daysComplete[idx];
                  return (
                    <div key={idx} onClick={() => { setCurrentDayIndex(idx); setViewMode('day'); }}
                      className={`flex-1 text-center py-2 text-sm font-semibold border-r border-emerald-600 cursor-pointer transition
                        ${active ? 'bg-emerald-500 text-white' : 'hover:bg-emerald-800 text-emerald-100'}`}>
                      <div className="uppercase text-xs font-semibold">{name.substring(0, 3)}</div>
                      <div className="text-base">{complete ? '✅' : '❌'}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex overflow-x-auto overflow-y-hidden rounded-b">
                <div className="w-16 shrink-0 border-r border-gray-200 text-xs text-gray-400 select-none" style={{ height: slotHeight * TOTAL_SLOTS }}>
                  {Array.from({ length: 24 }).map((_, h) => (
                    <div key={h} style={{ height: slotHeight * 4 }} className="border-b border-gray-100 flex items-start px-1 pt-0.5">
                      {h.toString().padStart(2, '0')}:00
                    </div>
                  ))}
                </div>
                {REFERENCE_DATES.map((refDate, idx) => {
                  const prefix = refDate.replace(/-/g, '/');
                  const dayProgs = programmes
                    .filter(p => p.start.startsWith(prefix))
                    .sort((a, b) => safeParse(a.start).diff(safeParse(b.start)));
                  return (
                    <div key={refDate} className="flex-1 relative border-r border-gray-100"
                      style={{ height: slotHeight * TOTAL_SLOTS, minWidth: 80 }}
                      onClick={(e) => {
                        if (e.target !== e.currentTarget) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const y = e.clientY - rect.top;
                        const snapY = Math.round(y / slotHeight) * slotHeight;
                        setPendingAdd({ dayIdx: idx, y: snapY });
                      }}>
                      {Array.from({ length: 24 }).map((_, h) => (
                        <div key={h} className="absolute left-0 right-0 border-b border-gray-100 pointer-events-none"
                          style={{ top: h * slotHeight * 4, height: slotHeight * 4 }} />
                      ))}
                      {dayProgs.filter(p => positions[p.__internalId]).map((p, pidx) => renderProgram(p, pidx, true))}
                      {pendingAdd?.dayIdx === idx && (
                        <div className="absolute left-0.5 right-0.5 z-40" style={{ top: pendingAdd.y, height: slotHeight }}>
                          <div className="bg-green-100 text-green-800 px-1 py-0.5 rounded shadow flex justify-between items-center h-full text-xs border border-green-300 animate-pulse">
                            <span>Agregar</span>
                            <div className="flex gap-1">
                              <button onClick={(e) => { e.stopPropagation(); handleAddForDayIdx(idx, pendingAdd.y); }}
                                className="bg-green-600 text-white px-1 py-0.5 rounded hover:bg-green-700">✓</button>
                              <button onClick={(e) => { e.stopPropagation(); setPendingAdd(null); }}
                                className="text-red-500 hover:text-red-700">✕</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* DAY VIEW */}
        {viewMode === 'day' && (
          <div className="p-4">
            <h2 className="sticky top-0 z-40 text-center text-white font-semibold py-2 text-sm bg-emerald-700 border-b border-emerald-800 shadow-sm mb-3">{DAY_NAMES[currentDayIndex]}</h2>
            <div className="flex max-w-3xl mx-auto bg-white rounded shadow overflow-hidden border border-gray-200">
              <div className="w-20 border-r border-gray-200 text-xs text-gray-500 select-none">
                {Array.from({ length: TOTAL_SLOTS }).map((_, i) => {
                  const minutes = i * SNAP_INTERVAL_MIN;
                  const hour = Math.floor(minutes / 60);
                  const min = minutes % 60;
                  const label = `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;
                  const slotStart = minutes;
                  const slotEnd = minutes + SNAP_INTERVAL_MIN;
                  const occupied = currentDateProgs.some(p => {
                    const s = safeParse(p.start);
                    if (!s) return false;
                    const sm = s.hour() * 60 + s.minute();
                    return slotStart < sm + getDurationMinutes(p) && slotEnd > sm;
                  });
                  return (
                    <div key={i} style={{ height: slotHeight }} className="px-2 py-1 border-b border-gray-100 flex items-center justify-between group">
                      {label}
                      {!occupied && (
                        <button onClick={() => setPendingAddY(i * slotHeight)}
                          className="text-green-600 opacity-0 group-hover:opacity-100 transition-opacity text-sm ml-auto">＋</button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex-1 relative" style={{ height: slotHeight * TOTAL_SLOTS }}>
                {currentDateProgs
                  .filter(p => positions[p.__internalId])
                  .sort((a, b) => safeParse(a.start).diff(safeParse(b.start)))
                  .map((p, idx) => renderProgram(p, idx, false))}
                {pendingAddY !== null && (
                  <div className="absolute left-0 right-0 px-2" style={{ top: pendingAddY, height: slotHeight }}>
                    <div className="bg-green-100 text-green-800 px-2 py-1 rounded shadow flex justify-between items-center h-full text-sm border border-green-300 animate-pulse">
                      <span>Agregar programa</span>
                      <div className="flex gap-2">
                        <button onClick={handleAdd} className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700">Agregar</button>
                        <button onClick={() => setPendingAddY(null)} className="text-red-500 text-xs hover:text-red-700">Cancelar</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Editar programa</h2>
            <div className="mb-4">
              <label className="block font-semibold text-sm mb-1 text-gray-700">Título</label>
              <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-1">
                <label className="font-semibold text-sm text-gray-700">Descripción</label>
                <span className={`text-xs ${editDesc.length >= MAX_DESC_LENGTH ? 'text-red-600 font-semibold' : editDesc.length >= MAX_DESC_LENGTH - 25 ? 'text-amber-600' : 'text-gray-400'}`}>
                  {editDesc.length}/{MAX_DESC_LENGTH}
                </span>
              </div>
              <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} maxLength={MAX_DESC_LENGTH}
                className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm resize-y min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="bg-gray-300 text-gray-800 px-5 py-2 rounded-md hover:bg-gray-400">Cancelar</button>
              <button onClick={saveEdit} className="bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {showDiffModal && diffData && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Diferencias con la programación actual</h2>
              <p className="text-sm text-gray-500 mt-1">
                La plantilla guardada difiere de la programación en las siguientes semanas. ¿Qué deseas hacer?
              </p>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {diffData.weeks.filter(w => w.has_differences).map(week => (
                <div key={week.monday} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 font-semibold text-sm text-gray-700">
                    {week.label}
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500 uppercase tracking-wide">
                        <th className="px-3 py-1.5 text-left">Día</th>
                        <th className="px-3 py-1.5 text-left">Hora</th>
                        <th className="px-3 py-1.5 text-left text-red-600">Actual</th>
                        <th className="px-3 py-1.5 text-left text-emerald-700">Plantilla</th>
                      </tr>
                    </thead>
                    <tbody>
                      {week.differences.map((diff, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-1.5 text-gray-600">{diff.day_name}</td>
                          <td className="px-3 py-1.5 text-gray-500 font-mono">{diff.start_time}</td>
                          <td className="px-3 py-1.5 text-red-700">
                            {diff.existing_title
                              ? `${diff.existing_title} (${diff.existing_duration_min}min)`
                              : <span className="italic text-gray-400">vacío</span>}
                          </td>
                          <td className="px-3 py-1.5 text-emerald-800 font-medium">
                            {diff.template_title} ({diff.template_duration_min}min)
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={handleDiffDismiss}
                className="bg-gray-200 text-gray-800 px-5 py-2 rounded-md hover:bg-gray-300 text-sm">
                Mantener diferencias
              </button>
              <button onClick={handleDiffOverwrite}
                className="bg-emerald-600 text-white px-5 py-2 rounded-md hover:bg-emerald-700 text-sm font-medium">
                Sobrescribir programación
              </button>
            </div>
          </div>
        </div>
      )}
      <HelpModal open={showHelp} onClose={() => setShowHelp(false)} accent="emerald" />
    </>
  );
}
